import { captureException } from '@sentry/nextjs'

import { CreditCardProductCategory } from '@appTypes/EligibilityApi.interface'

import AnimatedLoadingPage from '@components/AnimatedLoadingPage'
import ErrorBoundary from '@components/ErrorBoundary'
import { DynamicFrequentlyAskedQuestions } from '@components/FrequentlyAskedQuestions'
import TermDescriptions from '@components/TermDescriptions'

import useAppQueryState from '@hooks/useAppQueryState'

import { useUnscoredCategoriesQuery } from '@queries/useUnscoredCategoriesQuery'

import UnscoredBalanceTransferPage from './components/UnscoredBalanceTransferPage'
import UnscoredCashbackPurchasePage from './components/UnscoredCashbackPage'
import UnscoredCombinedPage from './components/UnscoredCombinedPage'
import UnscoredPageHeader from './components/UnscoredPageHeader'
import UnscoredPoorCreditPage from './components/UnscoredPoorCreditPage'
import UnscoredRewardsPage from './components/UnscoredRewardsPage'
import UnscoredTable from './components/UnscoredTable'
import UnscoredTableError from './components/UnscoredTableError'
import UnscoredTableSkeleton from './components/UnscoredTableSkeleton'
import UnscoredTravelPage from './components/UnscoredTravelPage'
import UnscoredZeroPercentPurchasePage from './components/UnscoredZeroPercentPurchasePage'

const UnscoredTableRoute = () => {
    const { isReady, appQueryState } = useAppQueryState()

    const unscoredCategoriesResponse = useUnscoredCategoriesQuery({
        select: data => data,
    })

    if (!isReady) {
        return <AnimatedLoadingPage />
    }

    if (
        appQueryState.categoryId === CreditCardProductCategory.BalanceTransfer
    ) {
        return <UnscoredBalanceTransferPage />
    }

    if (appQueryState.categoryId === CreditCardProductCategory.Cashback) {
        return <UnscoredCashbackPurchasePage />
    }

    if (appQueryState.categoryId === CreditCardProductCategory.Combined) {
        return <UnscoredCombinedPage />
    }

    if (appQueryState.categoryId === CreditCardProductCategory.PoorCredit) {
        return <UnscoredPoorCreditPage />
    }

    if (appQueryState.categoryId === CreditCardProductCategory.Purchase) {
        return <UnscoredZeroPercentPurchasePage />
    }

    if (appQueryState.categoryId === CreditCardProductCategory.Rewards) {
        return <UnscoredRewardsPage />
    }

    if (appQueryState.categoryId === CreditCardProductCategory.Travel) {
        return <UnscoredTravelPage />
    }

    return (
        <>
            <UnscoredPageHeader activeCategoryId={appQueryState.categoryId!} />
            <ErrorBoundary
                fallback={<UnscoredTableError />}
                onError={({ error }) => captureException(error)}
            >
                <>
                    {unscoredCategoriesResponse.isLoading ? (
                        <UnscoredTableSkeleton />
                    ) : null}

                    {unscoredCategoriesResponse.isSuccess ? (
                        <UnscoredTable
                            unscoredCategories={
                                unscoredCategoriesResponse.data!
                            }
                            activeCategoryId={appQueryState.categoryId!}
                        />
                    ) : null}

                    {unscoredCategoriesResponse.isError ? (
                        <UnscoredTableError />
                    ) : null}

                    <TermDescriptions
                        showMatchFactorDescription={false}
                        showCreditCardTypesDescription={true}
                        showPreApprovedDescription={true}
                        showExclusiveDescription={true}
                        showVariableAprDescription={false}
                        showPrePopulation={true}
                        showSeeParticularCardDescription={true}
                    />

                    {unscoredCategoriesResponse.isSuccess ? (
                        <DynamicFrequentlyAskedQuestions
                            category={appQueryState.categoryId!}
                        />
                    ) : null}
                </>
            </ErrorBoundary>
        </>
    )
}

export default UnscoredTableRoute
